import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ThemeSwitcher from './components/ThemeSwitcher';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';
import FacturationPage from './pages/FacturationPage';
import PrestationsPage from './pages/PrestationsPage';
import ClientsPage from './pages/ClientsPage';
import DossiersPage from './pages/DossiersPage';
import EquipePage from './pages/EquipePage';
import ChildrenPage from './pages/ChildrenPage';
import MealsPage from './pages/MealsPage';
import SportPage from './pages/SportPage';
import ShoppingPage from './pages/ShoppingPage';
import HabitsPage from './pages/HabitsPage';
import CalendarPage from './pages/CalendarPage';
import NotesPage from './pages/NotesPage';

const NAV = [
  { section: 'QUOTIDIEN' },
  { path: '/', label: 'Ma Journée', emoji: '☀️' },
  { path: '/taches', label: 'Tâches', emoji: '📋' },
  { path: '/calendrier', label: 'Calendrier', emoji: '📅' },
  { section: 'VIE PROFESSIONNELLE' },
  { path: '/prestations', label: 'Prestations', emoji: '⏱️' },
  { path: '/facturation', label: 'Facturation', emoji: '💶' },
  { path: '/clients', label: 'Clients', emoji: '👥' },
  { path: '/dossiers', label: 'Dossiers', emoji: '📁' },
  { path: '/equipe', label: 'Équipe', emoji: '👔', adminOnly: true },
  { section: 'VIE DE FAMILLE' },
  { path: '/enfants', label: 'Enfants', emoji: '👶' },
  { path: '/repas', label: 'Repas', emoji: '🍽️' },
  { path: '/courses', label: 'Courses', emoji: '🛒' },
  { section: 'BIEN-ÊTRE' },
  { path: '/sport', label: 'Sport', emoji: '🏋️' },
  { path: '/habitudes', label: 'Habitudes', emoji: '🌟' },
  { path: '/notes', label: 'Notes', emoji: '📝' },
];

function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <>
      <div className={`sidebar-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <nav className={`sidebar ${open ? 'open' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">&#128197;</div>
            OrgaLife
          </div>
          {open && <button className="hamburger" onClick={onClose}>&#10005;</button>}
        </div>
        <div className="sidebar-subtitle">Toute ta vie, une seule app</div>

        <div className="sidebar-nav">
          {NAV.map((item, i) => {
            if (item.section) {
              return <div key={i} className="sidebar-section">{item.section}</div>;
            }
            if (item.adminOnly && user.role !== 'admin') return null;
            return (
              <button
                key={item.path}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => { navigate(item.path); onClose(); }}
              >
                <span className="nav-emoji">{item.emoji}</span>
                {item.label}
              </button>
            );
          })}
        </div>

        <ThemeSwitcher />

        <div className="sidebar-user">
          <div className="sidebar-user-name">{user.name}</div>
          <div className="sidebar-user-email">{user.email}</div>
          <button className="sidebar-logout" onClick={logout}>&#128682; Déconnexion</button>
        </div>
      </nav>
    </>
  );
}

function AppLayout() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setSidebarOpen(false); }, [location]);

  if (!user) return <AuthPage />;

  const currentPage = NAV.find(n => n.path === location.pathname);
  const isPopup = new URLSearchParams(location.search).get('popup') === '1';

  // Mode popup : pas de sidebar, pas de header mobile, padding réduit
  if (isPopup) {
    return (
      <main style={{ padding: 20, maxWidth: 800, margin: '0 auto' }}>
        <Routes>
          <Route path="/taches/:id" element={<TaskDetailPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    );
  }

  return (
    <div className="app-layout">
      <div className="mobile-header">
        <button className="hamburger" onClick={() => setSidebarOpen(true)}>&#9776;</button>
        <span className="mobile-header-title">{currentPage?.emoji} {currentPage?.label || 'OrgaLife'}</span>
        <div style={{ width: 32 }} />
      </div>

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="main-content">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/taches" element={<TasksPage />} />
          <Route path="/taches/:id" element={<TaskDetailPage />} />
          <Route path="/prestations" element={<PrestationsPage />} />
          <Route path="/facturation" element={<FacturationPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/dossiers" element={<DossiersPage />} />
          <Route path="/equipe" element={<EquipePage />} />
          <Route path="/calendrier" element={<CalendarPage />} />
          <Route path="/enfants" element={<ChildrenPage />} />
          <Route path="/repas" element={<MealsPage />} />
          <Route path="/courses" element={<ShoppingPage />} />
          <Route path="/sport" element={<SportPage />} />
          <Route path="/habitudes" element={<HabitsPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </AuthProvider>
  );
}
